import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Crosshair, ExternalLink } from 'lucide-react';
import { formatDateTime, formatNumber } from '@/components/live';
import { FileUpload } from '@/components/FileUpload';
import { FarmerPhoto, usePhotoUrls } from '@/components/FarmerPhoto';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { can } from '@/auth/session';
import { toast } from '@/lib/toast';
import type { UploadedDocument } from '@/api/documents';
import type { FarmerRow } from '@/types/records';

/**
 * The farmer's photograph and the GPS point of their house (owner, 2026-09-21).
 *
 * The photo goes to the private `farmer-photos` bucket and is then pointed at from the farmer;
 * the same photo is shown against every plot the farmer holds. The house point is taken from
 * the device's own GPS while the officer is standing at the house - it is never typed in, and
 * the server refuses a point outside India, which is what a swapped latitude/longitude produces.
 */
export function FarmerProfile({ farmer }: { farmer: FarmerRow }) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const mayEdit = can(profile, 'farmer.register', 'update');
  const photo = usePhotoUrls([farmer.photo_document_id]);
  const [locating, setLocating] = useState(false);
  const [changingPhoto, setChangingPhoto] = useState(false);

  async function attachPhoto(document: UploadedDocument) {
    try {
      await callRpc('fn_set_farmer_photo', { p_farmer_id: farmer.id, p_document_id: document.id });
      await queryClient.invalidateQueries({ queryKey: ['farmers'] });
      await queryClient.invalidateQueries({ queryKey: ['farm_plots'] });
      setChangingPhoto(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The photograph could not be attached.');
    }
  }

  function captureHouse() {
    if (!('geolocation' in navigator)) {
      toast.error('This device cannot read its location.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        callRpc('fn_set_farmer_house_location', {
          p_farmer_id: farmer.id, p_lat: latitude, p_lng: longitude, p_accuracy_m: Math.round(accuracy),
        })
          .then(() => queryClient.invalidateQueries({ queryKey: ['farmers'] }))
          .then(() => toast.success(`House location saved (within about ${Math.round(accuracy)} m).`))
          .catch((error: unknown) => toast.error(error instanceof Error ? error.message : 'The location could not be saved.'))
          .finally(() => setLocating(false));
      },
      (error) => {
        setLocating(false);
        toast.error(error.code === error.PERMISSION_DENIED
          ? 'Location permission was refused. Allow location for this site and try again.'
          : 'The location could not be read. Step outside, wait a moment for GPS, and try again.');
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  }

  const hasHouse = farmer.house_lat != null && farmer.house_lng != null;
  const mapLink = hasHouse ? `https://www.google.com/maps?q=${farmer.house_lat},${farmer.house_lng}` : null;

  return (
    <div className="farmer-profile">
      <div className="farmer-profile-photo">
        <FarmerPhoto name={farmer.full_name} size={88}
                     url={farmer.photo_document_id ? photo.data?.[farmer.photo_document_id] : null} />
        {mayEdit && !changingPhoto && (
          <button onClick={() => setChangingPhoto(true)} type="button">
            {farmer.photo_document_id ? 'Change photo' : 'Add photo'}
          </button>
        )}
      </div>

      <div className="farmer-profile-body">
        {changingPhoto && profile?.organization && (
          <FileUpload
            accept="image/jpeg,image/png,image/webp,image/heic"
            bucket="farmer-photos"
            label="Farmer photograph"
            onUploaded={(document: UploadedDocument) => void attachPhoto(document)}
            organizationId={profile.organization.id}
            ownerId={farmer.id}
          />
        )}

        <div className="farmer-profile-house">
          <span className="muted">House location (GPS)</span>
          {hasHouse ? (
            <div>
              <strong>{formatNumber(farmer.house_lat, 6)}, {formatNumber(farmer.house_lng, 6)}</strong>
              {farmer.house_accuracy_meters != null && <span className="muted"> ± {formatNumber(farmer.house_accuracy_meters)} m</span>}
              {farmer.house_captured_at && <div className="muted">Captured {formatDateTime(farmer.house_captured_at)}</div>}
              {mapLink && (
                <a href={mapLink} rel="noreferrer" target="_blank">
                  Open in map <ExternalLink size={12} />
                </a>
              )}
            </div>
          ) : <span>Not captured yet.</span>}
          {mayEdit && (
            <button disabled={locating} onClick={captureHouse}
                    title="Stand at the farmer's house, then capture" type="button">
              <Crosshair size={15} /> {locating ? 'Reading GPS...' : hasHouse ? 'Re-capture at the house' : 'Capture at the house'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
